// ============================================================
// THE AMORPHOUS MASS ON THE PLATE (plan body-map P7; the goo study, 2026-10-09).
//
// The mass is not traced art: it is soft drops fused into one surface (a blur and a hard alpha threshold, in the
// plate's filters). This is its shape at rest - the study's `body()` with its clock at zero and nothing happening to
// it: two cores at the centre and nine satellite drops round them, so the plate drawn still shows the settled goo.
// The motion is gooLive's (S2): this is its moment 0, without the stray drop.
//
// The dimension lines read the SHEET, not the screen: at rest the mass is its Size's height tall (the app's own
// SIZE_HEIGHTS) and its marker diameter wide, or as wide as it is tall.
//
// Pure. Zero React dependency. Frame: the study's, ~1000 x 760 with the ground at y 650.
// ============================================================

import { SIZE_HEIGHTS } from '../constants';
import { CALM, FOOT, gooBody } from './gooLive';

/** One drop: centre x, centre y, radius. */
export type Drop = readonly [number, number, number];

/** The settled mass: the moving mass (gooLive) at time 0, calm, without its stray drop. */
export function gooAtRest(): { cores: Drop[]; sats: Drop[] } {
  const { cores, sats } = gooBody(0, 0, CALM, true);
  return { cores, sats };
}

/** The drops' box and their centre of mass, weighted by area - where the callout's sight sits. */
export function gooBounds(drops: readonly Drop[]) {
  let w = 0;
  let mx = 0;
  let my = 0;
  for (const [x, y, r] of drops) {
    const a = r * r;
    w += a;
    mx += x * a;
    my += y * a;
  }
  return {
    x0: Math.min(...drops.map(([x, , r]) => x - r)),
    x1: Math.max(...drops.map(([x, , r]) => x + r)),
    y0: Math.min(...drops.map(([, y, r]) => y - r)),
    y1: Math.max(...drops.map(([, y, r]) => y + r)),
    mx: mx / w,
    my: my / w,
  };
}

/** The mass's size in metres at rest: its Size's height tall, its marker diameter (or its height) wide. */
export function gooMetres(size: string, tokenDiameter: number | undefined): { width: number; height: number } {
  const height = SIZE_HEIGHTS[size] ?? SIZE_HEIGHTS.Average;
  return { width: tokenDiameter && tokenDiameter > 0 ? tokenDiameter : height, height };
}

/**
 * A drop as one of the mass's layers draws it: pulled toward the core and shrunk for an inner wall - and lifted as it
 * goes in, contour lines over a dome seen from a little above.
 */
export function pulledDrop([x, y, r]: Drop, [cx, cy]: Drop, pull: number, size: number): Drop {
  return [cx + (x - cx) * (1 - pull), cy + (y - cy) * (1 - pull) - 60 * pull, Math.max(0, r * size)];
}

/** How far the moving mass reaches past its rest box: a stray drop's 80 and a swelling drop's growth. */
const WOBBLE = 90;

/**
 * The mass's frame (plan plate-unboxed: every body fills its tab): its rest box grown by the wobble's reach, the
 * dimension lines and their words to its left and under it, the MASS callout out to the upper right (its numeral
 * ends by x 680, its name's cap at y 100), the projector's ring under it, and - while it says something - the status
 * line at y 750; with a margin of 6 %. Frame units are the study's (ground at y 650).
 */
export function gooFrame(box: ReturnType<typeof gooBounds>, status: boolean) {
  const x0 = Math.min(box.x0 - WOBBLE, box.x0 - 34 - 40);
  const x1 = Math.max(box.x1 + WOBBLE, 680);
  const y0 = Math.min(box.y0 - WOBBLE, 100);
  const y1 = Math.max(box.y1 + 34 + 30, FOOT + 50, status ? 770 : 0);
  const m = 0.06 * Math.max(x1 - x0, y1 - y0);
  return { x: x0 - m, y: y0 - m, w: x1 - x0 + 2 * m, h: y1 - y0 + 2 * m };
}
